import React, { useState, useEffect } from 'react';
import { 
  Bot, Shield, Zap, CheckCircle2, 
  ArrowRight, Globe, BarChart3, 
  Mail, MessageCircle, ChevronDown, Activity, Users, Clock,
  Lock, Server, Terminal
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

// Интерфейс для тикера
interface TickerData {
  symbol: string;
  price: string;
  change: string;
}

export const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoggedIn = !!localStorage.getItem('token');

  // --- 1. РЕАЛЬНЫЕ ЦЕНЫ (BINANCE API) ---
  const [tickers, setTickers] = useState<TickerData[]>([
    { symbol: 'BTC', price: '...', change: '0.00' },
    { symbol: 'ETH', price: '...', change: '0.00' },
    { symbol: 'SOL', price: '...', change: '0.00' },
    { symbol: 'BNB', price: '...', change: '0.00' },
    { symbol: 'XRP', price: '...', change: '0.00' },
  ]);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await fetch('https://api.binance.com/api/v3/ticker/24hr');
        const data = await res.json();
        
        const targetCoins = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT'];
        const updates = data
          .filter((item: any) => targetCoins.includes(item.symbol))
          .map((item: any) => ({
            symbol: item.symbol.replace('USDT', ''),
            price: parseFloat(item.lastPrice).toLocaleString('en-US', { maximumFractionDigits: 2 }),
            change: parseFloat(item.priceChangePercent).toFixed(2)
          }));
        
        if (updates.length > 0) setTickers(updates);
      } catch (e) {
        console.error("Ticker fetch error", e);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 10000); // Обновляем каждые 10 сек
    return () => clearInterval(interval);
  }, []);

  // --- 2. АНИМАЦИЯ БЕЗОПАСНОСТИ ---
  const [securityScore, setSecurityScore] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecurityScore((prev) => (prev >= 100 ? 100 : prev + 1));
    }, 25);
    const stepInterval = setInterval(() => {
      setActiveStep((prev) => (prev < 4 ? prev + 1 : 4));
    }, 800);
    return () => { clearInterval(interval); clearInterval(stepInterval); };
  }, []);

  const securitySteps = [
    { label: 'Blockchain Nodes', status: 'done' },
    { label: 'AES-256 Encryption', status: 'done' },
    { label: 'Liquidity Pools', status: 'done' },
    { label: 'System Integrity', status: 'active' },
  ];

  // --- 3. СЛАЙДЕР (Увеличенный) ---
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    "/slides/1.jpg",  // Ваше фото 1
    "/slides/2.jpg",  // Ваше фото 2
    "/slides/3.jpg",  // Ваше фото 3
    "/slides/4.jpg",  // Ваше фото 4
    "/slides/5.jpg"   // Ваше фото 5
  ];

  useEffect(() => {
    const slideInterval = setInterval(() => setCurrentSlide(p => (p + 1) % slides.length), 4000);
    return () => clearInterval(slideInterval);
  }, []);

  const scrollToSection = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
        const offset = el.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: offset, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 font-sans selection:bg-blue-500/30 overflow-x-hidden">
      
      {/* --- INLINE STYLES FOR MARQUEE --- */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee-infinite {
          animation: marquee 40s linear infinite;
        }
      `}</style>

      {/* --- LIVE TICKER --- */}
      <div className="bg-slate-950 border-b border-slate-800 h-10 flex items-center overflow-hidden whitespace-nowrap fixed top-0 w-full z-[60]">
         <div className="flex animate-marquee-infinite min-w-full">
            <div className="flex items-center gap-12 px-6">
                {tickers.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs font-mono font-bold">
                        <span className="text-slate-300">{t.symbol}/USDT</span>
                        <span className={t.change.startsWith('-') ? 'text-red-400' : 'text-green-400'}>
                            ${t.price} ({t.change}%)
                        </span>
                    </div>
                ))}
                <div className="flex items-center gap-2 text-xs font-mono font-bold text-blue-400">
                    <Activity size={12} /> <span>SYSTEM: ONLINE</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono font-bold text-purple-400">
                    <Users size={12} /> <span>TRADERS: 1,240+</span>
                </div>
            </div>
            {/* DUPLICATE FOR SMOOTH LOOP */}
            <div className="flex items-center gap-12 px-6">
                {tickers.map((t, i) => (
                    <div key={`dup-${i}`} className="flex items-center gap-2 text-xs font-mono font-bold">
                        <span className="text-slate-300">{t.symbol}/USDT</span>
                        <span className={t.change.startsWith('-') ? 'text-red-400' : 'text-green-400'}>
                            ${t.price} ({t.change}%)
                        </span>
                    </div>
                ))}
                <div className="flex items-center gap-2 text-xs font-mono font-bold text-blue-400">
                    <Activity size={12} /> <span>SYSTEM: ONLINE</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono font-bold text-purple-400">
                    <Users size={12} /> <span>TRADERS: 1,240+</span>
                </div>
            </div>
         </div>
      </div>

      {/* --- HEADER --- */}
      <header className="fixed top-10 w-full z-50 bg-[#0f172a]/90 backdrop-blur-md border-b border-slate-800 transition-all duration-300">
        <div className="max-w-[90rem] mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-xl shadow-lg shadow-blue-900/20">
               <Bot className="w-6 h-6 text-white" /> 
            </div>
            <span className="text-xl font-bold text-white tracking-tight hidden sm:inline">TradingBot<span className="text-blue-500">Pro</span></span>
          </div>
          
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="hover:text-blue-400 transition-colors cursor-pointer">{t('landing.nav.howItWorks')}</a>
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')} className="hover:text-blue-400 transition-colors cursor-pointer">{t('landing.nav.features')}</a>
            <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')} className="hover:text-blue-400 transition-colors cursor-pointer">{t('landing.nav.pricing')}</a>
            <a href="#faq" onClick={(e) => scrollToSection(e, 'faq')} className="hover:text-blue-400 transition-colors cursor-pointer">{t('landing.nav.faq')}</a>
            <div className="w-px h-4 bg-slate-700 mx-2"></div>
            <LanguageSwitcher />
          </nav>

          <div className="flex items-center gap-4">
            <div className="md:hidden"><LanguageSwitcher /></div>
            {isLoggedIn ? (
               <button onClick={() => navigate('/platform')} className="bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-full text-sm font-bold transition-all shadow-lg flex items-center gap-2">
                  <Zap size={16} fill="currentColor" /> <span className="hidden sm:inline">{t('landing.nav.terminal')}</span>
               </button>
            ) : (
               <>
                 <button onClick={() => navigate('/login')} className="text-sm font-bold hover:text-white transition-colors hidden sm:block">{t('landing.nav.login')}</button>
                 <button onClick={() => navigate('/register')} className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-full text-sm font-bold transition-all shadow-lg shadow-blue-600/20 flex items-center gap-2">
                   {t('landing.nav.start')} <ArrowRight size={16} />
                 </button>
               </>
            )}
          </div>
        </div>
      </header>

      {/* --- HERO SECTION --- */}
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] -z-10 animate-pulse"></div>
        
        <div className="max-w-[90rem] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
          <div className="text-center lg:text-left animate-in slide-in-from-left-4 duration-700">
             
             <div className="flex flex-wrap gap-3 mb-8 justify-center lg:justify-start">
                <MarketStatus city="London" offset={0} />
                <MarketStatus city="New York" offset={-5} />
                <MarketStatus city="Tokyo" offset={9} />
             </div>

             <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wider mb-6">
                <Zap size={14} /> {t('landing.hero.badge')}
             </div>
             <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight tracking-tight">
               {t('landing.hero.title')} <br />
               <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">{t('landing.hero.subtitle')}</span>
             </h1>
             <p className="text-xl text-slate-400 max-w-2xl mx-auto lg:mx-0 mb-10 leading-relaxed">
               {t('landing.hero.desc')}
             </p>
             <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <button onClick={() => navigate('/register')} className="w-full sm:w-auto px-8 py-4 bg-white text-slate-900 font-bold rounded-2xl hover:scale-105 transition-transform flex items-center justify-center gap-2 shadow-xl shadow-white/10">
                   {t('landing.hero.cta')} <ArrowRight size={20}/>
                </button>
                <button onClick={() => navigate('/login')} className="w-full sm:w-auto px-8 py-4 bg-slate-800 border border-slate-700 text-white font-bold rounded-2xl hover:bg-slate-700 transition-colors flex items-center justify-center gap-2">
                   <Globe size={18}/> {t('landing.hero.demo')}
                </button>
             </div>
          </div>

          <div className="relative w-full max-w-md mx-auto lg:ml-auto animate-in slide-in-from-right-4 duration-700 delay-200">
             <div className="bg-[#1e293b]/90 backdrop-blur-xl border border-slate-700 p-6 rounded-[32px] shadow-2xl relative z-10 overflow-hidden">
                <div className="flex justify-between items-center mb-6 pb-6 border-b border-slate-700/50">
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.5)]"></div>
                        <span className="font-bold text-sm text-white tracking-wide">SYSTEM GUARD</span>
                    </div>
                    <div className="text-[10px] font-mono text-slate-500 bg-slate-800 px-2 py-1 rounded">V7.2.0 LIVE</div>
                </div>
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold mb-1 tracking-wider">Security Level</p>
                        <h3 className="text-5xl font-black text-white">{securityScore}%</h3>
                    </div>
                    <div className="w-20 h-20 rounded-full border-4 border-slate-800 flex items-center justify-center relative">
                        <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 36 36">
                            <path className="text-blue-500 transition-all duration-100 ease-out" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={`${securityScore}, 100`} />
                        </svg>
                        <Shield className="text-blue-500" size={28} />
                    </div>
                </div>
                <div className="space-y-3 mb-6">
                    {securitySteps.map((step, idx) => (
                        <div key={idx} className={`flex items-center gap-3 transition-all duration-500 ${idx <= activeStep ? 'opacity-100 translate-x-0' : 'opacity-30 -translate-x-4'}`}>
                            {idx < activeStep ? <CheckCircle2 size={16} className="text-green-500 shrink-0" /> : idx === activeStep ? <Activity size={16} className="text-blue-500 animate-spin shrink-0" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-700 shrink-0" />}
                            <span className="text-xs font-mono text-slate-300 uppercase">{step.label}</span>
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                        <div className="text-[9px] text-slate-500 uppercase font-bold flex items-center gap-1"><Server size={10}/> Uptime</div>
                        <div className="text-sm font-bold text-white">99.99%</div>
                    </div>
                    <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                        <div className="text-[9px] text-slate-500 uppercase font-bold flex items-center gap-1"><Lock size={10}/> Encrypted</div>
                        <div className="text-sm font-bold text-green-400">Yes</div>
                    </div>
                </div>
             </div>
             <div className="absolute -top-10 -right-10 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl -z-10 animate-pulse"></div>
          </div>
        </div>

        {/* === SLIDER === */}
        <div className="w-full max-w-[90rem] mx-auto px-6 mt-12">
            <div className="relative rounded-[32px] overflow-hidden border border-slate-800 shadow-2xl bg-[#1e293b]/50 group">
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-transparent to-transparent z-10 pointer-events-none"></div>
                <div className="relative aspect-[21/9] md:aspect-[24/9] overflow-hidden bg-slate-900">
                    {slides.map((slide, index) => (
                        <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                            <img src={slide} alt={`Preview ${index}`} className="w-full h-full object-cover object-top opacity-90 group-hover:scale-105 transition-transform duration-[10s]" />
                        </div>
                    ))}
                </div>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                    {slides.map((_, index) => (
                        <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2 h-2 rounded-full transition-all ${index === currentSlide ? 'bg-blue-500 w-6' : 'bg-slate-500 hover:bg-slate-300'}`} />
                    ))}
                </div>
                <div className="absolute bottom-8 left-8 z-20 hidden md:block">
                    <div className="bg-slate-900/80 backdrop-blur-md px-4 py-2 rounded-xl border border-slate-700/50 flex items-center gap-2">
                        <Terminal size={14} className="text-blue-400" />
                        <span className="text-xs font-mono text-slate-300">Live Platform Interface</span>
                    </div>
                </div>
            </div>
        </div>

        {/* STATS */}
        <div className="max-w-7xl mx-auto px-6 mt-20">
           <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-8 border-y border-slate-800/50 bg-slate-900/20 backdrop-blur-sm rounded-3xl">
              <StatItem value="$4.2M+" label={t('landing.stats.volume')} icon={<Activity size={18} className="text-green-500"/>} />
              <StatItem value="1,250+" label={t('landing.stats.traders')} icon={<Users size={18} className="text-blue-500"/>} />
              <StatItem value="24/7" label={t('landing.stats.uptime')} icon={<Clock size={18} className="text-purple-500"/>} />
              <StatItem value="0.05s" label={t('landing.stats.speed')} icon={<Zap size={18} className="text-yellow-500"/>} />
           </div>
        </div>
      </section>

      {/* --- HOW IT WORKS --- */}
      <section id="how-it-works" className="py-24 relative overflow-hidden scroll-mt-20">
         <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16">
               <div className="lg:w-1/2">
                  <h2 className="text-4xl font-black text-white mb-6">{t('landing.how.title')}</h2>
                  <p className="text-slate-400 mb-8 text-lg">{t('landing.how.subtitle')}</p>
                  <div className="space-y-8">
                     <StepItem num="01" title={t('landing.how.step1.title')} desc={t('landing.how.step1.desc')} />
                     <StepItem num="02" title={t('landing.how.step2.title')} desc={t('landing.how.step2.desc')} />
                     <StepItem num="03" title={t('landing.how.step3.title')} desc={t('landing.how.step3.desc')} />
                  </div>
               </div>
               <div className="lg:w-1/2 bg-gradient-to-br from-slate-800 to-slate-900 rounded-[40px] p-8 border border-slate-700 shadow-2xl relative overflow-hidden group">
                  <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="bg-[#0f172a] rounded-2xl p-6 border border-slate-800 mb-4 relative z-10">
                     <div className="flex justify-between items-center mb-4">
                        <span className="text-slate-400 text-sm font-bold">{t('landing.fake.mode')}</span>
                        <div className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded font-bold animate-pulse">ACTIVE</div>
                     </div>
                     <div className="flex gap-2">
                        <div className="flex-1 bg-blue-600 text-white py-3 rounded-xl text-center font-bold text-sm shadow-lg shadow-blue-600/20">TREND</div>
                        <div className="flex-1 bg-slate-800 text-slate-500 py-3 rounded-xl text-center font-bold text-sm">SCALP</div>
                     </div>
                  </div>
                  <div className="bg-[#0f172a] rounded-2xl p-6 border border-slate-800 relative z-10">
                     <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-400 text-xs font-bold">{t('landing.fake.risk')}</span>
                        <span className="text-white font-mono font-bold">1.5%</span>
                     </div>
                     <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full w-[30%] bg-purple-500 rounded-full"></div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* --- FEATURES --- */}
      <section id="features" className="py-24 bg-slate-900/50 scroll-mt-20 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
             <h2 className="text-3xl font-bold text-white mb-4">{t('landing.features.title')}</h2>
             <p className="text-slate-400">{t('landing.features.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
             <FeatureCard icon={<Globe className="text-blue-500"/>} title={t('landing.features.c1.title')} desc={t('landing.features.c1.desc')} />
             <FeatureCard icon={<Shield className="text-emerald-500"/>} title={t('landing.features.c2.title')} desc={t('landing.features.c2.desc')} />
             <FeatureCard icon={<BarChart3 className="text-purple-500"/>} title={t('landing.features.c3.title')} desc={t('landing.features.c3.desc')} />
          </div>
        </div>
      </section>

      {/* --- PRICING --- */}
      <section id="pricing" className="py-24 relative bg-slate-900/30 scroll-mt-20">
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-purple-600/10 rounded-full blur-[100px] -z-10"></div>
         <div className="max-w-7xl mx-auto px-6">
           <div className="text-center mb-16">
             <h2 className="text-4xl font-black text-white mb-4">{t('landing.pricing.title')}</h2>
             <p className="text-slate-400">{t('landing.pricing.subtitle')}</p>
           </div>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <PricingCard title={t('landing.pricing.p1.title')} price="$29" period={t('landing.pricing.period')} desc={t('landing.pricing.p1.desc')} features={[t('landing.pricing.p1.f1'), t('landing.pricing.p1.f2'), t('landing.pricing.p1.f3'), t('landing.pricing.p1.f4')]} btnText={t('landing.pricing.select')} />
              <PricingCard title={t('landing.pricing.p2.title')} price="$27" period={t('landing.pricing.period')} billing="$81 / 3 mo." save="SAVE 5%" desc={t('landing.pricing.p2.desc')} features={[t('landing.pricing.p2.f1'), t('landing.pricing.p2.f2'), t('landing.pricing.p2.f3'), t('landing.pricing.p2.f4')]} highlight btnText={t('landing.pricing.select')} />
              <PricingCard title={t('landing.pricing.p3.title')} price="$25" period={t('landing.pricing.period')} billing="$150 / 6 mo." save="SAVE 10%" desc={t('landing.pricing.p3.desc')} features={[t('landing.pricing.p3.f1'), t('landing.pricing.p3.f2'), t('landing.pricing.p3.f3'), t('landing.pricing.p3.f4')]} btnText={t('landing.pricing.select')} />
              <PricingCard title={t('landing.pricing.p4.title')} price="$23" period={t('landing.pricing.period')} billing="$276 / year" save="SAVE 20%" desc={t('landing.pricing.p4.desc')} features={[t('landing.pricing.p4.f1'), t('landing.pricing.p4.f2'), t('landing.pricing.p4.f3'), t('landing.pricing.p4.f4')]} btnText={t('landing.pricing.select')} />
           </div>
         </div>
      </section>

      {/* --- FAQ SECTION (2 Columns) --- */}
      <section id="faq" className="py-24 bg-slate-900/30 scroll-mt-20">
         <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
               <h2 className="text-3xl font-black text-white mb-4">{t('landing.faq.title')}</h2>
               <p className="text-slate-400">{t('landing.faq.subtitle')}</p>
            </div>
            {/* GRID LAYOUT ДЛЯ FAQ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
               {/* Left Column */}
               <div className="space-y-6">
                  <FAQItem question={t('landing.faq.q1.q')} answer={t('landing.faq.q1.a')} />
                  <FAQItem question={t('landing.faq.q2.q')} answer={t('landing.faq.q2.a')} />
                  <FAQItem question={t('landing.faq.q3.q')} answer={t('landing.faq.q3.a')} />
               </div>
               {/* Right Column */}
               <div className="space-y-6">
                  <FAQItem question={t('landing.faq.q4.q')} answer={t('landing.faq.q4.a')} />
                  <FAQItem question={t('landing.faq.q5.q')} answer={t('landing.faq.q5.a')} />
                  {/* Можно добавить 6-й вопрос для баланса, если нужно */}
               </div>
            </div>
         </div>
      </section>

      {/* --- CONTACT / SUPPORT --- */}
      <section id="contact" className="py-20 relative">
         <div className="max-w-4xl mx-auto px-6">
            <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/30 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
               <div className="absolute top-0 right-0 p-10 opacity-10 pointer-events-none">
                  <MessageCircle size={200} className="text-white"/>
               </div>
               <h2 className="text-3xl md:text-4xl font-black text-white mb-6">{t('landing.contact.title')}</h2>
               <p className="text-lg text-slate-300 mb-8 max-w-xl mx-auto">{t('landing.contact.desc')}</p>
               <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
                  <a href="https://t.me/pro_tradings_support_bot" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-3 bg-white text-blue-900 px-8 py-4 rounded-xl font-bold hover:bg-slate-100 transition-colors shadow-lg">
                     <MessageCircle size={20} /> {t('landing.contact.telegram')}
                  </a>
                  <a href="mailto:support@tradingbot.pro" className="flex items-center justify-center gap-3 bg-slate-800 text-white border border-slate-700 px-8 py-4 rounded-xl font-bold hover:bg-slate-700 transition-colors">
                     <Mail size={20} /> {t('landing.contact.email')}
                  </a>
               </div>
            </div>
         </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="border-t border-slate-800 py-12 bg-[#020617]">
         <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Bot size={20} className="text-blue-500" />
              <span className="font-bold text-white">TradingBotPro © 2026</span>
            </div>
            <div className="flex gap-6 text-sm text-slate-500">
               <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">{t('landing.footer.terms')}</button>
               <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">{t('landing.footer.privacy')}</button>
               <a href="https://t.me/pro_tradings_support_bot" className="hover:text-white transition-colors">{t('landing.footer.support')}</a>
            </div>
         </div>
      </footer>
    </div>
  );
};

// --- SUB COMPONENTS ---
const MarketStatus = ({ city, offset }: any) => {
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const cityTime = new Date(utc + (3600000 * offset));
    const hours = cityTime.getHours();
    
    // Простая логика торговых сессий (9:00 - 17:00)
    const isOpen = hours >= 9 && hours < 17;
    const status = isOpen ? 'OPEN' : 'CLOSED';
    const color = isOpen ? 'text-green-400' : 'text-red-400';

    return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-700">
            <Clock size={12} className="text-slate-400" />
            <span className="text-xs font-bold text-slate-300">{city}:</span>
            <span className={`text-xs font-black ${color}`}>{status}</span>
        </div>
    );
};

const StatItem = ({ value, label, icon }: any) => (
   <div className="flex flex-col items-center">
      <div className="flex items-center gap-2 mb-1 text-slate-200 font-bold text-xl">{icon} {value}</div>
      <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">{label}</span>
   </div>
);

const FeatureCard = ({ icon, title, desc }: any) => (
  <div className="bg-[#1e293b] p-8 rounded-3xl border border-slate-800 hover:border-slate-600 transition-colors group">
      <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">{icon}</div>
      <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-slate-400 leading-relaxed">{desc}</p>
  </div>
);

const StepItem = ({ num, title, desc }: any) => (
   <div className="flex gap-6">
      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center text-blue-400 font-black text-xl">
         {num}
      </div>
      <div>
         <h4 className="text-xl font-bold text-white mb-2">{title}</h4>
         <p className="text-slate-400">{desc}</p>
      </div>
   </div>
);

const FAQItem = ({ question, answer }: any) => (
   <details className="group bg-[#1e293b] rounded-2xl border border-slate-800 overflow-hidden open:border-blue-500/50 transition-all h-fit">
      <summary className="flex cursor-pointer list-none items-center justify-between p-6 font-bold text-white focus:outline-none">
         {question}
         <span className="transition group-open:rotate-180">
            <ChevronDown size={20} className="text-slate-500"/>
         </span>
      </summary>
      <div className="px-6 pb-6 text-slate-400 leading-relaxed animate-in slide-in-from-top-2">
         {answer}
      </div>
   </details>
);

const PricingCard = ({ title, price, period, billing, save, desc, features, highlight, btnText }: any) => (
  <div className={`relative p-8 rounded-3xl border flex flex-col h-full transition-transform duration-300 ${highlight ? 'bg-slate-800 border-blue-500 shadow-2xl shadow-blue-900/20 scale-105 z-10' : 'bg-[#1e293b] border-slate-800 hover:border-slate-700'}`}>
      {save && <div className="absolute top-0 right-0 bg-green-500 text-white text-[10px] font-black px-3 py-1 rounded-bl-xl rounded-tr-2xl uppercase">{save}</div>}
      <div className="mb-6">
         <h3 className={`text-lg font-bold ${highlight ? 'text-blue-400' : 'text-slate-200'}`}>{title}</h3>
         <p className="text-xs text-slate-500 mt-1">{desc}</p>
      </div>
      <div className="mb-6">
         <div className="flex items-end gap-1">
            <span className="text-4xl font-black text-white">{price}</span>
            <span className="text-slate-500 mb-1 font-medium">{period}</span>
         </div>
         {billing && <p className="text-xs text-slate-500 mt-2 font-mono">{billing}</p>}
      </div>
      <ul className="space-y-4 mb-8 flex-1">
         {features.map((f: string, i: number) => (
            <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
               <CheckCircle2 size={16} className="text-blue-500 shrink-0 mt-0.5" /> {f}
            </li>
         ))}
      </ul>
      <button className={`w-full py-4 rounded-xl font-bold transition-all ${highlight ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg' : 'bg-slate-900 text-white hover:bg-slate-800 border border-slate-700'}`}>
         {btnText}
      </button>
  </div>
);